import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/AuthContext";
import { ViewModeProvider } from "@/lib/ViewModeContext";
import { ThemeProvider } from "@/lib/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import LabelPrint from "@/pages/LabelPrint";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Sync from "@/pages/Sync";
import Productivity from "@/pages/Productivity";
import Wallboard from "@/pages/Wallboard";
import Paint from "@/pages/Paint";
import Inventory from "@/pages/Inventory";
import OrderShortLink from "@/pages/OrderShortLink";
import LabelShortLink from "@/pages/LabelShortLink";
import { Toaster } from "@/components/ui/sonner";
import WarehouseGate from "@/components/WarehouseGate";
import "@/App.css";

const ADMIN_ONLY = ["admin"];
const ADMIN_OR_MANAGER = ["admin", "manager"];

function LabelsIndex() {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Print</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Labels</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Pick an order from the <a href="/orders" className="text-[#C8102E] hover:underline font-semibold">Orders</a> page
          and tap <span className="font-semibold">Print Labels</span> to generate an Avery 5163 sheet.
        </p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <WarehouseGate>
        <AuthProvider>
          <ViewModeProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/wallboard"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Wallboard />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:id" element={<OrderDetail />} />
                {/* External deep-link resolver: accepts UUID, order_number, or
                    Base44 external_id and redirects to /orders/{uuid}. */}
                <Route path="/o/:ref" element={<OrderShortLink />} />
                {/* Same shape, but resolves to the Label Print page. */}
                <Route path="/l/:ref" element={<LabelShortLink />} />
                <Route path="/labels" element={<LabelsIndex />} />
                <Route path="/labels/:orderId" element={<LabelPrint />} />
                <Route path="/paint" element={<Paint />} />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute roles={ADMIN_OR_MANAGER}>
                      <Inventory />
                    </ProtectedRoute>
                  }
                />
                <Route path="/reports" element={<Reports />} />
                <Route
                  path="/productivity"
                  element={
                    <ProtectedRoute roles={ADMIN_ONLY}>
                      <Productivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sync"
                  element={
                    <ProtectedRoute roles={ADMIN_OR_MANAGER}>
                      <Sync />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={ADMIN_ONLY}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute roles={ADMIN_ONLY}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ViewModeProvider>
        <Toaster richColors position="top-right" />
      </AuthProvider>
      </WarehouseGate>
    </ThemeProvider>
  );
}

export default App;
